import { ActionIcon, Button, Menu, Badge, Text, Group, Tooltip } from '@mantine/core';
import { IconRefresh, IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';

type TimePeriod = 'today' | 'week' | 'month';

interface RefreshButtonProps {
  onRefresh?: (timePeriod: TimePeriod) => void;
  /** Render as a compact icon button (for mobile) */
  iconOnly?: boolean;
}

export function RefreshButton({ onRefresh, iconOnly }: RefreshButtonProps = {}) {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('today');

  const estimates = {
    today: '5-10 sec',
    week: '10-15 sec',
    month: '20-30 sec'
  };

  const labels = {
    today: 'Today',
    week: 'Last 7 Days',
    month: 'Last 30 Days'
  };

  const handleSelect = (period: TimePeriod) => {
    setTimePeriod(period);

    // Log to console for now (Task 10 will implement full functionality)
    console.log('Triggering refresh for:', period);

    // Call callback if provided
    if (onRefresh) {
      onRefresh(period);
    }
  };

  return (
    <Menu>
      <Menu.Target>
        {iconOnly ? (
          <Tooltip label="Refresh data" position="left" withArrow>
            <ActionIcon variant="light" size="lg" aria-label="Refresh data">
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Button
            rightSection={<IconChevronDown size={16} />}
            leftSection={<IconRefresh size={16} />}
            variant="light"
          >
            Refresh Data
            {timePeriod !== 'today' && (
              <Badge ml="xs" size="sm" variant="light">
                {timePeriod === 'week' ? '7d' : '30d'}
              </Badge>
            )}
          </Button>
        )}
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Select Time Period</Menu.Label>
        <Menu.Item onClick={() => handleSelect('today')}>
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text size="sm">{labels.today}</Text>
            <Text size="xs" c="dimmed">({estimates.today})</Text>
          </Group>
        </Menu.Item>
        <Menu.Item onClick={() => handleSelect('week')}>
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text size="sm">{labels.week}</Text>
            <Text size="xs" c="dimmed">({estimates.week})</Text>
          </Group>
        </Menu.Item>
        <Menu.Item onClick={() => handleSelect('month')}>
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text size="sm">{labels.month}</Text>
            <Text size="xs" c="orange">({estimates.month}) ⚠️</Text>
          </Group>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
